import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../api";
import { useCustomerId } from "context/CustomerIdContext";
import SlideOverPanel from "components/SlideOverPanel";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableMeta,
  MUIDataTableOptions,
} from "mui-datatables";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import {
  Edit as EditIcon,
  PauseCircleOutline,
  PlayCircleOutline,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "store";
import { formatMoney } from "utils/money";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

type AssetTypeOption = {
  asset_type_id: number;
  asset_type_name?: string;
};

type SubscriptionTypeOption = {
  subscription_type_id: number;
  subscription_type_name?: string;
  asset_type_id?: number;
  price?: number;
};

type AssetOption = {
  asset_id: number;
  asset_name?: string;
  is_active?: number;
};

type CustomPlanRow = {
  plan_id: number;
  plan_name: string | null;
  subscription_type_id: number;
  subscription_type_name?: string;
  asset_type_id?: number;
  custom_price: number;
  standard_price?: number;
  discount_per_asset?: number;
  billing_cycle: "one_time" | "monthly" | "yearly";
  status: "pending" | "active" | "expired" | "rejected";
  valid_from?: string | null;
  valid_until?: string | null;
  notes?: string | null;
  covered_asset_ids?: string | null;
  asset_count?: number;
};

type PlanActionsCellProps = {
  plan?: CustomPlanRow;
  onEdit: (plan: CustomPlanRow) => void;
  onExpire: (planId: number) => void;
  onActivate: (planId: number) => void;
};

const PlanActionsCell: React.FC<PlanActionsCellProps> = ({ plan, onEdit, onExpire, onActivate }) => {
  if (!plan) return null;
  const canPause = plan.status === "active" || plan.status === "pending";
  const canActivate = plan.status === "expired" || plan.status === "rejected";

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25 }}>
      <Tooltip title="Edit plan">
        <IconButton aria-label="edit" size="small" onClick={() => onEdit(plan)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {canPause && (
        <Tooltip title="Expire plan (customers see standard price until reactivated)">
          <IconButton aria-label="Expire plan" size="small" onClick={() => onExpire(plan.plan_id)}>
            <PauseCircleOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canActivate && (
        <Tooltip title="Activate plan">
          <IconButton
            aria-label="activate plan"
            size="small"
            color="success"
            onClick={() => onActivate(plan.plan_id)}
          >
            <PlayCircleOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

function parseCsvInts(value?: string | null): number[] {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((x) => Number(String(x).trim()))
    .filter((n) => Number.isFinite(n));
}

export default function ViewCustomPlans() {
  const customerId = useCustomerId();
  const user = useSelector((state: RootState) => state.auth.user);
  const appCurrency =
    (user as any)?.client_currency ||
    (user as any)?.currency_code ||
    "USD";
  const { fetchError, fetchSuccess } = useSnackbar();

  const formatAmount = useCallback(
    (value?: number | null) => {
      if (value == null || Number.isNaN(Number(value))) return "—";
      return formatMoney(Number(value), appCurrency);
    },
    [appCurrency],
  );

  const [plans, setPlans] = useState<CustomPlanRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [discountReport, setDiscountReport] = useState<any[]>([]);
  const [reconciliationReport, setReconciliationReport] = useState<any[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  const [assetTypesData, setAssetTypesData] = useState<AssetTypeOption[]>([]);
  const [subscriptionTypesForAsset, setSubscriptionTypesForAsset] = useState<SubscriptionTypeOption[]>(
    [],
  );
  const [availableAssets, setAvailableAssets] = useState<AssetOption[]>([]);

  const [panelOpen, setPanelOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editingPlan, setEditingPlan] = useState<CustomPlanRow | null>(null);

  const [pausePlanId, setPausePlanId] = useState<number | null>(null);

  const [form, setForm] = useState({
    asset_type_id: "" as string | number,
    subscription_type_id: "" as string | number,
    plan_name: "",
    custom_price: "",
    asset_ids: [] as number[],
  });

  const [formErrors, setFormErrors] = useState<{
    asset_type_id?: string;
    subscription_type_id?: string;
    plan_name?: string;
    custom_price?: string;
    asset_ids?: string;
  }>({});

  class FormValidationError extends Error {
    readonly field:
      | "asset_type_id"
      | "subscription_type_id"
      | "plan_name"
      | "custom_price"
      | "asset_ids";
    constructor(
      field:
        | "asset_type_id"
        | "subscription_type_id"
        | "plan_name"
        | "custom_price"
        | "asset_ids",
      message: string,
    ) {
      super(message);
      this.field = field;
    }
  }

  const selectedSubscriptionType = useMemo(() => {
    const id = Number(form.subscription_type_id);
    if (!Number.isFinite(id)) return null;
    return subscriptionTypesForAsset.find((s) => s.subscription_type_id === id) ?? null;
  }, [form.subscription_type_id, subscriptionTypesForAsset]);

  const selectedAssetType = useMemo(() => {
    const id = Number(form.asset_type_id);
    if (!Number.isFinite(id)) return null;
    return assetTypesData.find((a) => a.asset_type_id === id) ?? null;
  }, [form.asset_type_id, assetTypesData]);

  const fetchPlans = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/admin/custom-plans`, {
        params: { customer_id: customerId },
      });
      setPlans(res.data?.plans ?? []);
    } catch (e) {
      const err = e as AxiosError<{ error?: string }>;
      fetchError(err.response?.data?.error || "Failed to load custom plans");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetTypes = async () => {
    try {
      const res = await axios.get("/asset-types", { params: { is_active: 1 } });
      setAssetTypesData(res.data?.list ?? res.data ?? []);
    } catch (e) {
      const err = e as AxiosError<{ error?: string }>;
      fetchError(err.response?.data?.error || "Failed to load asset types");
    }
  };

  const fetchSubscriptionTypesForAsset = async (assetTypeId: number) => {
    try {
      const res = await axios.get(`/subscription-types/assets/${assetTypeId}`, {
        params: { is_active: 1 },
      });
      setSubscriptionTypesForAsset(res.data?.list ?? []);
    } catch (e) {
      const err = e as AxiosError<{ error?: string }>;
      fetchError(err.response?.data?.error || "Failed to load subscription types");
      setSubscriptionTypesForAsset([]);
    }
  };

  const fetchDiscountReport = async () => {
    if (!customerId) return;
    const res = await axios.get(`/admin/custom-plans/reports/discount`, {
      params: { customer_id: customerId },
    });
    setDiscountReport(res.data?.report ?? []);
  };

  const fetchReconciliationReport = async () => {
    if (!customerId) return;
    const res = await axios.get(`/admin/custom-plans/reports/reconciliation`, {
      params: { customer_id: customerId },
    });
    setReconciliationReport(res.data?.report ?? []);
  };

  useEffect(() => {
    fetchAssetTypes();
  }, []);

  useEffect(() => {
    if (!customerId) return;
    fetchPlans();
    setReportsLoading(true);
    Promise.allSettled([fetchDiscountReport(), fetchReconciliationReport()]).finally(() =>
      setReportsLoading(false),
    );
  }, [customerId]);

  useEffect(() => {
    const loadAssets = async () => {
      if (!customerId || !form.asset_type_id) {
        setAvailableAssets([]);
        return;
      }
      const atId = Number(form.asset_type_id);
      if (!Number.isFinite(atId)) {
        setAvailableAssets([]);
        return;
      }
      try {
        const res = await axios.get(`/assets/${atId}/${customerId}`);
        const list = res.data?.list ?? res.data ?? [];
        setAvailableAssets(
          Array.isArray(list)
            ? list.filter((a: AssetOption) => a?.is_active == null || a.is_active === 1)
            : [],
        );
      } catch {
        fetchError("Failed to load assets for this asset type");
        setAvailableAssets([]);
      }
    };
    loadAssets();
  }, [customerId, form.asset_type_id]);

  const openCreate = () => {
    setMode("create");
    setEditingPlan(null);
    setSubscriptionTypesForAsset([]);
    setForm({
      asset_type_id: "",
      subscription_type_id: "",
      plan_name: "",
      custom_price: "",
      asset_ids: [],
    });
    setPanelOpen(true);
  };

  const openEdit = (plan: CustomPlanRow) => {
    setMode("edit");
    setEditingPlan(plan);
    const assetIds = parseCsvInts(plan.covered_asset_ids);
    const atId = plan.asset_type_id;
    if (atId != null && Number.isFinite(Number(atId))) {
      fetchSubscriptionTypesForAsset(Number(atId));
    } else {
      setSubscriptionTypesForAsset([]);
    }
    setForm({
      asset_type_id: atId ?? "",
      subscription_type_id: plan.subscription_type_id,
      plan_name: plan.subscription_type_name ?? plan.plan_name ?? "",
      custom_price: String(plan.custom_price ?? ""),
      asset_ids: assetIds,
    });
    setPanelOpen(true);
  };

  const confirmPausePlan = async () => {
    if (pausePlanId == null) return;
    try {
      await axios.patch(`/admin/custom-plans/${pausePlanId}/status`, { status: "expired" });
      fetchSuccess("Plan paused. Customers see standard pricing until you activate it again.");
      setPausePlanId(null);
      await fetchPlans();
    } catch (e) {
      const err = e as AxiosError<{ error?: string }>;
      fetchError(err.response?.data?.error || "Failed to update plan");
    }
  };

  const handleActivate = async (planId: number) => {
    try {
      await axios.patch(`/admin/custom-plans/${planId}/status`, { status: "active" });
      fetchSuccess("Plan activated. A new 2-day customer offer window has started.");
      await fetchPlans();
    } catch (e) {
      const err = e as AxiosError<{ error?: string }>;
      fetchError(err.response?.data?.error || "Failed to activate plan");
    }
  };

  const ensurePositiveNumber = (
    value: number,
    field:
      | "asset_type_id"
      | "subscription_type_id"
      | "custom_price",
    message: string,
  ) => {
    if (!Number.isFinite(value) || value <= 0) {
      throw new FormValidationError(field, message);
    }
  };

  const ensurePlanNameProvided = (planName: string) => {
    if (!planName.trim()) {
      throw new FormValidationError("plan_name", "Plan name is required");
    }
  };

  const ensureAssetsSelected = (assets: number[]) => {
    if (!Array.isArray(assets) || assets.length === 0) {
      throw new FormValidationError(
        "asset_ids",
        "At least one asset must be selected",
      );
    }
  };

  const requireEditingPlanId = () => {
    if (!editingPlan?.plan_id) {
      throw new Error("No plan selected for edit");
    }
    return editingPlan.plan_id;
  };

  const validateFormInputs = () => {
    if (!customerId) throw new Error("Customer id missing");
    const subscriptionTypeId = Number(form.subscription_type_id);
    const customPrice = Number(form.custom_price);
    const trimmedPlanName = form.plan_name.trim();

    if (mode === "create") {
      const assetTypeId = Number(form.asset_type_id);
      ensurePositiveNumber(assetTypeId, "asset_type_id", "Asset type is required");
    }

    ensurePositiveNumber(
      subscriptionTypeId,
      "subscription_type_id",
      "Subscription type is required",
    );
    ensurePlanNameProvided(trimmedPlanName);
    ensurePositiveNumber(customPrice, "custom_price", "Custom price must be > 0");

    const standardPrice =
      mode === "edit" && editingPlan?.standard_price != null
        ? Number(editingPlan.standard_price)
        : selectedSubscriptionType?.price != null
          ? Number(selectedSubscriptionType.price)
          : null;

    if (standardPrice != null && Number.isFinite(standardPrice)) {
      if (customPrice >= standardPrice) {
        throw new FormValidationError(
          "custom_price",
          "Custom price must be less than the standard price",
        );
      }
    }

    if (form.custom_price && String(form.custom_price).replace(/[^\d]/g, "").length > 10) {
      throw new FormValidationError(
        "custom_price",
        "Custom price is too large",
      );
    }

    ensureAssetsSelected(form.asset_ids);

    return { subscriptionTypeId, customPrice, trimmedPlanName };
  };

  const createPlan = async (subscriptionTypeId: number, planName: string, customPrice: number) => {
    await axios.post(`/admin/custom-plans`, {
      customer_id: Number(customerId),
      subscription_type_id: subscriptionTypeId,
      plan_name: planName,
      custom_price: customPrice,
      asset_ids: form.asset_ids,
    });
  };

  const updatePlan = async (planId: number, planName: string, customPrice: number) => {
    await axios.put(`/admin/custom-plans/${planId}`, {
      plan_name: planName,
      custom_price: customPrice,
      valid_from: null,
      valid_until: null,
      notes: null,
      asset_ids: form.asset_ids,
    });
  };

  const submit = async () => {
    try {
      setFormErrors({});
      const { subscriptionTypeId, customPrice, trimmedPlanName } = validateFormInputs();
      if (mode === "create") {
        await createPlan(subscriptionTypeId, trimmedPlanName, customPrice);
        fetchSuccess("Custom plan created");
      } else {
        const planId = requireEditingPlanId();
        await updatePlan(planId, trimmedPlanName, customPrice);
        fetchSuccess("Custom plan updated");
      }
      setPanelOpen(false);
      await fetchPlans();
    } catch (e) {
      if (e instanceof FormValidationError) {
        setFormErrors((prev) => ({ ...prev, [e.field]: e.message }));
        return;
      }
      const err = e as AxiosError<{ error?: string }>;
      fetchError(
        err.response?.data?.error ||
          (e instanceof Error ? e.message : "") ||
          "Failed to submit plan",
      );
    }
  };

  const standardPriceDisplay =
    mode === "edit" && editingPlan?.standard_price != null
      ? editingPlan.standard_price
      : selectedSubscriptionType?.price;

  const planColumns: MUIDataTableColumn[] = [
    { name: "plan_name", label: "Plan" },
    { name: "subscription_type_name", label: "Type" },
    {
      name: "standard_price",
      label: "Standard",
      options: { customBodyRender: (v: number) => formatAmount(v) },
    },
    {
      name: "custom_price",
      label: "Custom",
      options: { customBodyRender: (v: number) => formatAmount(v) },
    },
    {
      name: "discount_per_asset",
      label: "Discount / asset",
      options: { customBodyRender: (v: number) => formatAmount(v) },
    },
    { name: "asset_count", label: "Assets" },
    { name: "status", label: "Status" },
    {
      name: "plan_id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_value: any, tableMeta: MUIDataTableMeta) => (
          <PlanActionsCell
            plan={plans?.[tableMeta.rowIndex] as CustomPlanRow | undefined}
            onEdit={openEdit}
            onExpire={setPausePlanId}
            onActivate={handleActivate}
          />
        ),
      },
    },
  ];

  const discountColumns: MUIDataTableColumn[] = [
    { name: "customer_name", label: "Customer" },
    { name: "subscription_type_name", label: "Subscription Type" },
    { name: "plan_name", label: "Plan" },
    { name: "revision_number", label: "Rev" },
    {
      name: "standard_price",
      label: "Standard",
      options: { customBodyRender: (v: number) => formatAmount(v) },
    },
    {
      name: "custom_price",
      label: "Custom",
      options: { customBodyRender: (v: number) => formatAmount(v) },
    },
    {
      name: "discount_per_asset",
      label: "Discount / asset",
      options: { customBodyRender: (v: number) => formatAmount(v) },
    },
    { name: "assets_in_plan", label: "Assets" },
    {
      name: "total_discount_given",
      label: "Total discount",
      options: { customBodyRender: (v: number) => formatAmount(v) },
    },
    { name: "status", label: "Status" },
    {
      name: "valid_from",
      label: "Valid From",
      options: {
        customBodyRender: (v: any) => (v ? dayjs(v).format("YYYY-MM-DD") : "—"),
      },
    },
    {
      name: "valid_until",
      label: "Valid Until",
      options: {
        customBodyRender: (v: any) => (v ? dayjs(v).format("YYYY-MM-DD") : "—"),
      },
    },
  ];

  const reconciliationColumns: MUIDataTableColumn[] = [
    { name: "order_id", label: "Order ID" },
    { name: "payment_id", label: "Payment ID" },
    { name: "customer", label: "Customer" },
    { name: "subscription_type_name", label: "Subscription Type" },
    {
      name: "original_price",
      label: "Standard",
      options: { customBodyRender: (v: number) => formatAmount(v) },
    },
    {
      name: "charged_price",
      label: "Charged",
      options: { customBodyRender: (v: number) => formatAmount(v) },
    },
    {
      name: "discount_applied",
      label: "Discount",
      options: { customBodyRender: (v: number) => formatAmount(v) },
    },
    { name: "status", label: "Status" },
    {
      name: "created_at",
      label: "Payment Date",
      options: {
        customBodyRender: (v: any) => (v ? dayjs(v).format("YYYY-MM-DD") : "—"),
      },
    },
  ];

  const tableOptions: MUIDataTableOptions = {
    selectableRows: "none",
    selectableRowsHeader: false,
    elevation: 0,
    responsive: "standard",
    tableBodyHeight: "auto",
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 2,
        }}
      >
        <SoftTypography variant="h5" fontWeight="bold">
          Custom plans
        </SoftTypography>
        <Button variant="contained" color="primary" onClick={openCreate} sx={{ textTransform: "none" }}>
          Create custom plan
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="custom-plans-table"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title="Plans for this customer"
              data={plans}
              columns={planColumns}
              options={tableOptions}
            />
          </ThemeProvider>
        </SoftBox>
      )}

      {reportsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4, mt: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <SoftBox
            sx={{
              mt: 3,
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": (theme: Theme) => ({
                  borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
                }),
              },
            }}
            className="custom-plans-discount-report"
          >
            <ThemeProvider theme={getMuiTheme}>
              <MUIDataTable
                title="Discount report"
                data={discountReport}
                columns={discountColumns}
                options={tableOptions}
              />
            </ThemeProvider>
          </SoftBox>
            <SoftBox
            sx={{
              mt: 3,
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": (theme: Theme) => ({
                  borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
                }),
              },
            }}
            className="custom-plans-reconciliation"
          >
            <ThemeProvider theme={getMuiTheme}>
              <MUIDataTable
                title="Payment reconciliation (custom vs standard)"
                data={reconciliationReport}
                columns={reconciliationColumns}
                options={tableOptions}
              />
            </ThemeProvider>
          </SoftBox>
        </>
      )}

      <Dialog open={pausePlanId != null} onClose={() => setPausePlanId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Expire this plan?</DialogTitle>
        <DialogContent>
          Expiring sets the plan to expired for pricing: customers pay the standard rate until you activate
          this plan again.
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => setPausePlanId(null)}
            sx={{ textTransform: "none", minWidth: 120 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={confirmPausePlan}
            sx={{ textTransform: "none", minWidth: 180, px: 4 }}
          >
            Expire
          </Button>
        </DialogActions>
      </Dialog>

      <SlideOverPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={mode === "create" ? "Create custom plan" : "Edit custom plan"}
      >
        <Box sx={{ flex: 1, py: 1 }}>
          <Grid container spacing={2}>
            {mode === "create" && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <SoftTypography variant="caption" fontWeight="bold" sx={{ pb: 0.75, display: "block" }}>
                    Asset type
                  </SoftTypography>
                  <Autocomplete
                    options={assetTypesData || []}
                    getOptionLabel={(o) => o.asset_type_name ?? String(o.asset_type_id)}
                    value={selectedAssetType}
                    onChange={(_e, value) => {
                      const nextAtId = value?.asset_type_id ?? "";
                      setFormErrors((prev) => ({ ...prev, asset_type_id: undefined }));
                      setForm((prev) => ({
                        ...prev,
                        asset_type_id: nextAtId,
                        subscription_type_id: "",
                        plan_name: "",
                        asset_ids: [],
                      }));
                      if (value?.asset_type_id != null) {
                        fetchSubscriptionTypesForAsset(value.asset_type_id);
                      } else {
                        setSubscriptionTypesForAsset([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select asset type"
                        error={!!formErrors.asset_type_id}
                        helperText={formErrors.asset_type_id}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            )}

            {mode === "create" && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <SoftTypography variant="caption" fontWeight="bold" sx={{ pb: 0.75, display: "block" }}>
                    Subscription type
                  </SoftTypography>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    value={form.subscription_type_id || ""}
                    disabled={!form.asset_type_id}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormErrors((prev) => ({
                        ...prev,
                        subscription_type_id: undefined,
                      }));
                      const st = subscriptionTypesForAsset.find(
                        (s) => String(s.subscription_type_id) === String(v),
                      );
                      setForm((prev) => ({
                        ...prev,
                        subscription_type_id: v,
                        plan_name: st?.subscription_type_name ?? "",
                        asset_ids: [],
                      }));
                    }}
                    SelectProps={{ native: true }}
                    error={!!formErrors.subscription_type_id}
                    helperText={formErrors.subscription_type_id}
                  >
                    <option value="">Select subscription type</option>
                    {subscriptionTypesForAsset.map((st) => (
                      <option key={st.subscription_type_id} value={st.subscription_type_id}>
                        {st.subscription_type_name ?? st.subscription_type_id}
                      </option>
                    ))}
                  </TextField>
                </FormControl>
              </Grid>
            )}

            {mode === "create" && form.plan_name ? (
              <Grid item xs={12}>
                <SoftTypography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                  Plan name (saved as): <strong>{form.plan_name}</strong>
                </SoftTypography>
              </Grid>
            ) : null}

            {mode === "edit" && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ pb: 0.75, display: "block" }}>
                      Asset type
                    </SoftTypography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={
                        selectedAssetType?.asset_type_name ??
                        (form.asset_type_id ? String(form.asset_type_id) : "")
                      }
                      InputProps={{ readOnly: true }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ pb: 0.75, display: "block" }}>
                      Subscription type
                    </SoftTypography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={form.plan_name || editingPlan?.subscription_type_name || ""}
                      InputProps={{ readOnly: true }}
                    />
                  </FormControl>
                </Grid>
              </>
            )}

            {(mode === "create" ? !!selectedSubscriptionType : true) && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <SoftTypography variant="caption" fontWeight="bold" sx={{ pb: 0.75, display: "block" }}>
                    Standard price
                  </SoftTypography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={
                      standardPriceDisplay != null
                        ? formatAmount(standardPriceDisplay as number)
                        : "—"
                    }
                    InputProps={{ readOnly: true }}
                  />
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ pb: 0.75, display: "block" }}>
                  Custom price
                </SoftTypography>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  inputProps={{ min: 0, step: "0.01", maxLength: 14 }}
                  placeholder={`Amount in ${appCurrency}`}
                  value={form.custom_price}
                  onChange={(e) => {
                    setFormErrors((prev) => ({ ...prev, custom_price: undefined }));
                    setForm((prev) => ({ ...prev, custom_price: e.target.value }));
                  }}
                  error={!!formErrors.custom_price}
                  helperText={formErrors.custom_price}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ pb: 0.75, display: "block" }}>
                  Assets covered
                </SoftTypography>
                <Autocomplete
                  multiple
                  disabled={!form.asset_type_id}
                  options={availableAssets}
                  value={availableAssets.filter((a) => form.asset_ids.includes(a.asset_id))}
                  getOptionLabel={(o) => o.asset_name ?? String(o.asset_id)}
                  onChange={(_e, values) => {
                    setFormErrors((prev) => ({ ...prev, asset_ids: undefined }));
                    setForm((prev) => ({
                      ...prev,
                      asset_ids: values.map((v) => v.asset_id),
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select assets"
                      error={!!formErrors.asset_ids}
                      helperText={formErrors.asset_ids}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}>
                <Button variant="outlined" color="primary" onClick={() => setPanelOpen(false)} sx={{ textTransform: "none" }}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={submit} sx={{ textTransform: "none" }}>
                  {mode === "create" ? "Create plan" : "Save changes"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </SlideOverPanel>
    </Box>
  );
}
